import { TIKTOK_WIDGET_NAMES } from "@/constants/widget-names";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { parseIntDataAttr } from "@ui-utils/Injector/utils";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import TikTokGalleryWidget from "./TikTokGalleryWidget";

const OLD_GALLERY_ID_DATA_ATTR = "gallery_id";
const GALLERY_ID_DATA_ATTR = "gw-gallery-id";

class TikTokGalleryWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger?.debug("TikTokGalleryWidgetInjector.start", {
            placeholderSelector: this.placeholderSelector,
        });
        this.ioi.targets$.subscribe((placeholder) => {
            this.logger?.debug(
                "TikTokGalleryWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            let galleryId = parseIntDataAttr(placeholder, GALLERY_ID_DATA_ATTR);
            if (galleryId === null) {
                galleryId = parseIntDataAttr(
                    placeholder,
                    OLD_GALLERY_ID_DATA_ATTR
                );

                if (galleryId !== null) {
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                            TIKTOK_WIDGET_NAMES.TIKTOK_GALLERY_WIDGET,
                            OLD_GALLERY_ID_DATA_ATTR
                        )
                    );
                }
            }

            if (galleryId === null)
                throw new Error(
                    `Placehoder does not have data-${GALLERY_ID_DATA_ATTR} attribute.`
                );

            const widget = new TikTokGalleryWidget(
                {
                    target: placeholder,
                    galleryId: galleryId,
                },
                this.logger
            );

            widget.init();

            this.eventsCollector.pushEvent(
                this.analyticsEventsFactory.createWidgetInjected(
                    TIKTOK_WIDGET_NAMES.TIKTOK_GALLERY_WIDGET
                )
            );
            dispatchSdkEvent(
                new WidgetInjectedSdkEvent(
                    TIKTOK_WIDGET_NAMES.TIKTOK_GALLERY_WIDGET
                )
            );
        });

        this.ioi.start();
    }
}

export default TikTokGalleryWidgetInjector;
